import { match } from "ts-pattern";
import { clamp } from "../../lib/utils";
import { toRotation } from "./viewerSession";

/** A box in PDF points, origin bottom-left (pdf.js's own space): `[x1, y1, x2, y2]`. */
export type PdfRect = [x1: number, y1: number, x2: number, y2: number];

export type Highlight = {
	/** Unique per highlight. */
	id: string;
	/** The selection key; every highlight of a field shares it. */
	fieldId: string;
	/** One-based page. */
	page: number;
	/** `text` marks words on the page; `region` outlines an area such as a box or a table cell. */
	kind: "text" | "region";
	/** One rect per line for multi-line text. */
	rects: PdfRect[];
	/** The field's name, for screen readers only. Without it they hear just the page. */
	label?: string;
};

/** A box as fractions (0–1) of the rendered page, origin top-left. */
export type PageBox = { left: number; top: number; width: number; height: number };

/** A page's `view` (its media box): `[x0, y0, x1, y1]` in PDF points. */
export type PageViewBox = readonly number[];

const clampUnit = (value: number) => clamp(value, 0, 1);

const rotatePoint = (u: number, v: number, rotation: number): [number, number] =>
	match(toRotation(rotation))
		.returnType<[number, number]>()
		.with(0, () => [u, v])
		.with(90, () => [1 - v, u])
		.with(180, () => [1 - u, 1 - v])
		.with(270, () => [v, 1 - u])
		.exhaustive();

const viewDims = (viewBox: PageViewBox) => {
	const [x0 = 0, y0 = 0, x1 = 0, y1 = 0] = viewBox;
	return { x0, y1, width: x1 - x0 || 1, height: y1 - y0 || 1 };
};

/**
 * Where a rect sits on the page as drawn at `rotation` (clockwise degrees), as fractions of the
 * rendered page. Fractions don't change with zoom, so highlights only move when rotation does.
 * A box that spills off the page is clamped to it and flagged.
 */
export const toPageBox = (
	rect: PdfRect,
	viewBox: PageViewBox,
	rotation: number,
): { box: PageBox; clamped: boolean } => {
	const { x0, y1, width, height } = viewDims(viewBox);
	const [ax, ay] = rotatePoint((rect[0] - x0) / width, (y1 - rect[1]) / height, rotation);
	const [bx, by] = rotatePoint((rect[2] - x0) / width, (y1 - rect[3]) / height, rotation);
	const left = Math.min(ax, bx);
	const top = Math.min(ay, by);
	const right = Math.max(ax, bx);
	const bottom = Math.max(ay, by);
	const clampedLeft = clampUnit(left);
	const clampedTop = clampUnit(top);
	const clampedRight = clampUnit(right);
	const clampedBottom = clampUnit(bottom);
	return {
		box: {
			left: clampedLeft,
			top: clampedTop,
			width: clampedRight - clampedLeft,
			height: clampedBottom - clampedTop,
		},
		clamped:
			clampedLeft !== left ||
			clampedTop !== top ||
			clampedRight !== right ||
			clampedBottom !== bottom,
	};
};

/** One box around them all. */
export const unionBox = (boxes: PageBox[]): PageBox | undefined => {
	if (boxes.length === 0) return undefined;
	const left = Math.min(...boxes.map((box) => box.left));
	const top = Math.min(...boxes.map((box) => box.top));
	const right = Math.max(...boxes.map((box) => box.left + box.width));
	const bottom = Math.max(...boxes.map((box) => box.top + box.height));
	return { left, top, width: right - left, height: bottom - top };
};

/**
 * Converts a box given as 0–1 fractions of the unrotated page, origin top-left, into PDF
 * points. Use it in an adapter at ingest, so every highlight is stored the same way.
 */
export const normalizedBoxToPdfRect = (box: PageBox, viewBox: PageViewBox): PdfRect => {
	const { x0, y1, width, height } = viewDims(viewBox);
	return [
		x0 + box.left * width,
		y1 - (box.top + box.height) * height,
		x0 + (box.left + box.width) * width,
		y1 - box.top * height,
	];
};
